import { createEmailHistoryApiInstance, getForwardedHost } from 'utils/emailHistory/apiUtils';

/**
 * Scheduled Emails API Client
 * Base URL: /api3.2/canmail
 */

// Get the canmail API instance
const canmailApi = createEmailHistoryApiInstance('/api3.2/canmail');

/**
 * List all scheduled emails with optional status filtering
 * @param {Object} params - Query parameters
 * @param {string} [params.status] - Filter by status ('Pending', 'Processing', 'Failed')
 * @param {number} [params.limit=100] - Maximum number of records to return
 * @returns {Promise<{scheduled_emails: Array, total_count: number, filters: Object}>}
 */
export async function fetchScheduledEmails(params = {}) {
  const {
    status = null,
    limit = 100,
  } = params;

  const queryParams = new URLSearchParams();
  
  if (status) {
    queryParams.set('status', status);
  }
  
  if (limit) {
    queryParams.set('limit', String(limit));
  }

  try {
    const endpoint = `/scheduled?${queryParams.toString()}`;

    const { data } = await canmailApi.get(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch scheduled emails');
    }

    // Return both data and stats from the API response
    return {
      ...data.data,
      stats: data.stats || null,
    };
  } catch (error) {
    console.error('Error fetching scheduled emails:', error);
    throw error;
  }
}

/**
 * Get complete details for a specific scheduled email
 * @param {number} campaignId - The scheduled email ID (ScheduleID)
 * @returns {Promise<Object>} Scheduled email details
 */
export async function fetchScheduledEmailDetails(campaignId) {
  try {
    const endpoint = `/scheduled/${campaignId}`;

    const { data } = await canmailApi.get(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch scheduled email details');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching scheduled email details:', error);
    throw error;
  }
}

/**
 * Update an existing scheduled email
 * Only emails with 'Pending' status can be updated
 * @param {number} campaignId - The scheduled email ID (ScheduleID)
 * @param {Object} updateData - Fields to update (all optional)
 * @param {number} [updateData.list_id] - List ID
 * @param {string} [updateData.to] - Recipient email (for individual emails)
 * @param {string} [updateData.from] - Sender email address
 * @param {string} [updateData.reply_to] - Reply-to email address(es)
 * @param {string} [updateData.subject] - Email subject line
 * @param {string} [updateData.email_contents] - Final HTML email content
 * @param {string} [updateData.content_text] - Text editor HTML content
 * @param {string} [updateData.content_drop] - Unlayer editor base64 JSON
 * @param {Array} [updateData.attachments] - Array of attachment objects (replaces all)
 * @param {string} [updateData.schedule_date] - ISO 8601 date string
 * @returns {Promise<{schedule_id: number, old_schedule_id: number, message: string}>}
 */
export async function updateScheduledEmail(campaignId, updateData) {
  try {
    const endpoint = `/scheduled/${campaignId}`;

    const { data } = await canmailApi.put(endpoint, updateData, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to update scheduled email');
    }

    return data.data;
  } catch (error) {
    console.error('Error updating scheduled email:', error);
    throw error;
  }
}

/**
 * Cancel (delete) a scheduled email
 * Only emails with 'Pending' status can be cancelled
 * @param {number} campaignId - The scheduled email ID (ScheduleID)
 * @returns {Promise<{campaign_id: number, subject: string, message: string}>}
 */
export async function cancelScheduledEmail(campaignId) {
  try {
    const endpoint = `/scheduled/${campaignId}`;

    const { data } = await canmailApi.delete(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to cancel scheduled email');
    }

    return data.data;
  } catch (error) {
    console.error('Error cancelling scheduled email:', error);
    throw error;
  }
}

